import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiRest } from '../../api/constants';
import { LuClock3 } from 'react-icons/lu';
import { WEEK_DAYS, dayHours, StoreHours, storeSchedules } from '../storeHours';

const Context = createContext({ ready: true, canOrder: true, restricted: false, check: async () => true });
export const useShopAvailability = () => useContext(Context);
const AvailabilitySession = ({ children }) => {
  const [state, setState] = useState({ ready: false, canOrder: false });
  const active = useRef(true);
  const pending = useRef(null);
  const check = useCallback(() => {
    if (pending.current) return pending.current;
    pending.current = (async () => {
      try {
        const response = await fetch(apiRest + 'ordering-availability', { credentials: 'include', cache: 'no-store' });
        const result = await response.json();
        if (!response.ok || result?.error !== false || typeof result.data?.canOrder !== 'boolean') throw new Error('Availability unavailable');
        if (active.current) setState({ ...result.data, ready: true });
        return active.current && result.data.canOrder;
      } catch {
        if (active.current) setState(previous => ({ ...previous, ready: true, canOrder: false, error: true, message: 'No se pudo comprobar el horario de pedidos. Vuelve a intentarlo.' }));
        return false;
      } finally { pending.current = null; }
    })();
    return pending.current;
  }, []);
  useEffect(() => {
    active.current = true; check();
    const timer = setInterval(check, 15000);
    const refresh = () => { if (!document.hidden) check(); };
    window.addEventListener('focus', refresh); document.addEventListener('visibilitychange', refresh);
    return () => { active.current = false; clearInterval(timer); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [check]);
  return <Context.Provider value={{ ...state, check }}>{children}</Context.Provider>;
};
export const ShopAvailabilityProvider = ({ children }) => {
  const info = useSelector(state => state.aplicationConfig.corporationData.info);
  const identity = info?.corporation?.corporationId || info?.corporationId || '';
  return <AvailabilitySession key={identity}>{children}</AvailabilitySession>;
};
export const ShopAvailabilityNotice = () => {
  const state = useShopAvailability();
  if (!state.ready) return <p className="eats-ordering-notice" role="status">Comprobando horario de pedidos…</p>;
  if (!state.restricted && !state.error) return null;
  return <div className={`eats-ordering-notice ${state.canOrder ? 'is-open' : 'is-closed'}`}>
    <LuClock3 aria-hidden="true" /><div><strong>{state.error ? 'Horario no disponible' : state.canOrder ? 'Pedidos abiertos' : 'Pedidos cerrados'}</strong><p role="status">{state.message}</p>
      {state.schedule && <details><summary>Ver horario de pedidos{state.demo ? ' de la demo' : ''}</summary><p>Zona horaria: {state.timeZone}</p><dl>{WEEK_DAYS.map(([day,label]) => <div key={day}><dt>{label}</dt><dd>{dayHours(state.schedule.dayInfo?.[day],state.schedule.isContinuous)}</dd></div>)}</dl></details>}
      {state.error && <button type="button" onClick={state.check}>Reintentar</button>}
    </div>
  </div>;
};

export const ShopAvailabilityHeader = () => {
  const state = useShopAvailability();
  const corporation = useSelector(store => store.aplicationConfig.corporationData.info?.corporation);
  if (!storeSchedules(corporation, state.restricted ? state.schedule : undefined).length && !state.restricted) return null;
  return <div className="eats-store-status">
    {!state.ready ? <span role="status" className="eats-availability-pending">Comprobando horario…</span>
      : state.error ? <span role="status" className="eats-availability-pending">Horario no disponible <button type="button" onClick={state.check}>Reintentar</button></span>
      : state.restricted ? <span role="status" className={`eats-availability-badge ${state.canOrder ? 'is-open' : 'is-closed'}`}><span aria-hidden="true" />{state.canOrder ? 'Abierto' : 'Cerrado'}</span> : null}
    <StoreHours orderingSchedule={state.restricted ? state.schedule : undefined} timeZone={state.timeZone} />
  </div>;
};
